import { useCallback } from 'react';
import { type AddressType } from '@sg/garnish';

import { useCheckoutBrazeLogPurchase } from '@order/features/ordering';

import { useBraze } from '../../../../context/Braze';
import { usePlacedOrdersCounter } from '../../../../context/PlacedOrdersCounter';
import {
  useInAppReview,
  useLocalOrderHistory,
  useOrderFeedbackNotifications,
  useOrderStatusNotificationPreferences,
} from '../../../../hooks';
import { useSnapPixel } from '../../../../integrations';

/**
 * Handles the actions that need to be taken post placing an order.
 */
export const usePostOrder = () => {
  const { scheduleOrderFeedbackNotification } = useOrderFeedbackNotifications();
  const { incrementPlacedOrdersCounter } = usePlacedOrdersCounter();
  const requestInAppReview = useInAppReview();
  const { addToLocalOrderHistory } = useLocalOrderHistory();
  const { isPushSubscribed } = useOrderStatusNotificationPreferences({ requestPolicy: 'cache-first' }); // prettier-ignore

  const { logPurchase } = useBraze();
  const logBrazePurchase = useCheckoutBrazeLogPurchase({ logPurchase });
  const { trackPurchase: trackSnapPixelPurchase } = useSnapPixel();

  return useCallback(
    (params: PostOrderParams) => {
      const {
        orderId,
        orderChannel,
        wantedTime,
        restaurantId,
        lineItems,
        total,
        subtotal,
        discountTotal,
      } = params;

      if (isPushSubscribed) {
        void scheduleOrderFeedbackNotification({ orderId, wantedTime });
      }

      addToLocalOrderHistory({ orderId });

      incrementPlacedOrdersCounter();

      requestInAppReview();

      logBrazePurchase({
        restaurantId,
        lineItems,
        orderChannel,
        total,
        subtotal,
        discountTotal,
      });

      trackSnapPixelPurchase({
        priceInDollars: total / 100,
        orderId,
        productIds: lineItems?.map((item) => item.product?.id ?? '') ?? [],
        lineItemsQuantity: lineItems?.length ?? 0,
      });
    },
    [
      isPushSubscribed,
      addToLocalOrderHistory,
      incrementPlacedOrdersCounter,
      requestInAppReview,
      logBrazePurchase,
      trackSnapPixelPurchase,
      scheduleOrderFeedbackNotification,
    ],
  );
};

// ─── Types ──────────────────────────────────────────────────────────────────

type PostOrderParams = {
  restaurantId: string;
  total: number;
  subtotal: number;
  discountTotal: number;
  lineItems: readonly LineItem[] | undefined | null;
  orderId: string;
  orderChannel: AddressType;
  wantedTime: string; // ISO date string
};

type LineItem = { product?: { id?: string; name?: string } };
